// Candidate-side proctoring: captures camera, mic and the entire screen, streams them
// peer-to-peer to admins watching live, and keeps the exam paused while the candidate is
// out of fullscreen, away from the window or on more than one display. Nothing is recorded.

import { stApi, iceServers } from './api'
import { PollingSignal, waitForIceGathering } from './signaling'

const isLive = (stream, kind) => !!stream && stream.getTracks().some(t => (!kind || t.kind === kind) && t.readyState === 'live')

// Only Chromium browsers (Chrome, Edge) report which surface was shared and whether more
// than one display is connected, so proctoring cannot be enforced anywhere else.
export function isSupportedBrowser() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) return false
  const brands = navigator.userAgentData?.brands
  if (brands) return brands.some(b => /Chromium|Google Chrome|Microsoft Edge/.test(b.brand))
  return /Chrome\/|Edg\//.test(navigator.userAgent) && !/Firefox|FxiOS|OPR\//.test(navigator.userAgent)
}

const hasMultipleDisplays = () => window.screen?.isExtended === true

export class ProctorSession {
  constructor({ onChange } = {}) {
    this.onChange = onChange || (() => {})
    this.camStream = null
    this.screenStream = null
    this.signal = null
    this.peers = new Map() // proctor peer id -> { pc, senders }
    this.listeners = []
    this.monitoring = false
    this.connected = false
    this.awaySince = null // when the exam window lost focus
    this.leftWindow = null // { seconds } until the candidate acknowledges coming back
  }

  acknowledgeReturn() {
    this.leftWindow = null
    this.onChange()
  }

  status() {
    return {
      camera: isLive(this.camStream, 'video'),
      mic: isLive(this.camStream, 'audio'),
      screen: isLive(this.screenStream, 'video'),
      fullscreen: !!document.fullscreenElement,
      singleDisplay: !hasMultipleDisplays(),
      leftWindow: this.leftWindow,
      connected: this.connected,
      proctors: this.peers.size
    }
  }

  ready() {
    const s = this.status()
    return s.camera && s.mic && s.screen && s.singleDisplay
  }

  // ─── Media ─────────────────────────────────────────────────────────────────

  async enableCamera() {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 15 } },
      audio: { echoCancellation: true, noiseSuppression: true }
    })
    this.camStream?.getTracks().forEach(t => t.stop())
    this.camStream = stream
    stream.getTracks().forEach(t => t.addEventListener('ended', () => this.onChange()))
    this.replaceTracks()
    this.onChange()
    return stream
  }

  async enableScreen() {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor', frameRate: { ideal: 5, max: 10 } },
      audio: false,
      monitorTypeSurfaces: 'include',
      selfBrowserSurface: 'exclude',
      surfaceSwitching: 'exclude'
    })
    const track = stream.getVideoTracks()[0]
    const surface = track.getSettings().displaySurface
    // A window or tab share hides everything else on screen. Browsers that do not report
    // the surface (Firefox, Safari) cannot prove it is the entire screen, so they are refused too.
    if (surface !== 'monitor') {
      stream.getTracks().forEach(t => t.stop())
      throw new Error(surface
        ? 'Please share your entire screen, not a window or a tab. Choose the "Entire screen" option.'
        : 'This browser cannot confirm that you shared your entire screen. Please use desktop Chrome or Edge.')
    }
    this.screenStream?.getTracks().forEach(t => t.stop())
    this.screenStream = stream
    track.addEventListener('ended', () => this.onChange())
    this.replaceTracks()
    this.onChange()
    return stream
  }

  // After re-enabling a device, swap the new track into every existing connection.
  replaceTracks() {
    const tracks = {
      camera: this.camStream?.getVideoTracks()[0],
      mic: this.camStream?.getAudioTracks()[0],
      screen: this.screenStream?.getVideoTracks()[0]
    }
    for (const { senders } of this.peers.values()) {
      for (const [key, sender] of Object.entries(senders)) {
        if (tracks[key] && sender.track !== tracks[key]) sender.replaceTrack(tracks[key]).catch(() => {})
      }
    }
  }

  // ─── On-screen checks (local only; nothing is sent or stored) ──────────────

  startMonitoring() {
    if (this.monitoring) return
    this.monitoring = true
    const on = (target, event, handler) => {
      target.addEventListener(event, handler)
      this.listeners.push(() => target.removeEventListener(event, handler))
    }
    on(window, 'blur', () => {
      if (!this.awaySince) this.awaySince = Date.now()
    })
    on(window, 'focus', () => {
      if (!this.awaySince) return
      const seconds = Math.max(1, Math.round((Date.now() - this.awaySince) / 1000))
      this.awaySince = null
      this.leftWindow = { seconds }
      this.onChange()
    })
    if (window.screen && 'onchange' in window.screen) on(window.screen, 'change', () => this.onChange())
    on(document, 'fullscreenchange', () => this.onChange())
  }

  // ─── Live streaming to proctors ────────────────────────────────────────────

  async connect() {
    const { data } = await stApi.post('/attempt/proctor-ticket')
    this.signal = new PollingSignal(data.ticket)
      .on('connect', () => { this.connected = true; this.onChange() })
      .on('disconnect', () => { this.connected = false; this.onChange() })
      .on('unauthorized', () => { this.connected = false; this.onChange() })
      .on('peer-joined', ({ peerId }) => this.safeCall(peerId))
      .on('peer-left', ({ peerId }) => this.closePeer(peerId))
      .on('signal', ({ from, data }) => this.handleSignal(from, data))
      .start()
  }

  safeCall(proctorId) {
    this.callProctor(proctorId).catch(err => console.warn('Could not connect to proctor', err))
  }

  async callProctor(proctorId) {
    this.closePeer(proctorId)
    if (!this.camStream || !this.screenStream) return
    const pc = new RTCPeerConnection({ iceServers: iceServers() })
    const peer = { pc, senders: {} }
    this.peers.set(proctorId, peer)

    const camVideo = this.camStream.getVideoTracks()[0]
    const mic = this.camStream.getAudioTracks()[0]
    const screen = this.screenStream.getVideoTracks()[0]
    if (camVideo) peer.senders.camera = pc.addTrack(camVideo, this.camStream)
    if (mic) peer.senders.mic = pc.addTrack(mic, this.camStream)
    if (screen) peer.senders.screen = pc.addTrack(screen, this.screenStream)

    pc.onconnectionstatechange = () => {
      // Retry while that proctor is still online.
      if (pc.connectionState === 'failed' && this.peers.get(proctorId)?.pc === pc && this.signal?.peers.has(proctorId)) {
        this.safeCall(proctorId)
      }
      this.onChange()
    }

    await pc.setLocalDescription(await pc.createOffer())
    await waitForIceGathering(pc)
    if (this.peers.get(proctorId)?.pc !== pc) return // replaced or closed meanwhile
    await this.signal.send(proctorId, {
      type: 'offer',
      sdp: pc.localDescription,
      streams: { [this.camStream.id]: 'camera', [this.screenStream.id]: 'screen' }
    })
    this.onChange()
  }

  async handleSignal(from, data) {
    const peer = this.peers.get(from)
    if (!peer || data?.type !== 'answer') return
    try {
      await peer.pc.setRemoteDescription(data.sdp)
    } catch (err) {
      console.warn('Proctoring signal error', err)
    }
  }

  closePeer(id) {
    const peer = this.peers.get(id)
    if (peer) {
      peer.pc.close()
      this.peers.delete(id)
      this.onChange()
    }
  }

  closeAllPeers() {
    for (const id of [...this.peers.keys()]) this.closePeer(id)
  }

  // ─── Teardown ──────────────────────────────────────────────────────────────

  async stop() {
    this.monitoring = false
    this.listeners.forEach(off => off())
    this.listeners = []
    this.closeAllPeers()
    this.signal?.stop()
    this.signal = null
    this.camStream?.getTracks().forEach(t => t.stop())
    this.screenStream?.getTracks().forEach(t => t.stop())
    this.camStream = null
    this.screenStream = null
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
  }
}
